import  { useEffect } from "react";
import { Form, Select, Flex, Radio, Button } from "antd";
import inputStyles from "../../../styles/Custom/Input.module.css";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { validateConfig } from "../../../config/validate.config";
import { getCookie } from "../../../helpers/localStorage";
import { Role } from "../../../constants/defaultKeys";

const { Option, OptGroup } = Select;

const CLINICAL_PREFIX = "clinical:";
const isClinicalValue = (v) =>
  typeof v === "string" && v.startsWith(CLINICAL_PREFIX);

const rolesWithIsHodQuestion = [
  "Doctor",
  "Nurse",
  "Physiotherapist",
  "Speech_Swallow_Therapist",
  "Respiratory_Therapist",
  "Nutritionist",
  "Psychologist",
];
const rolesWithoutHodField = [
  "Organization_Admin",
  "Organization_Owner",
  "Facility_Admin",
  "Receptionist",
];
const rolesWithFacility = [
  "Doctor",
  "Nurse",
  "Physiotherapist",
  "Speech_Swallow_Therapist",
  "Respiratory_Therapist",
  "Nutritionist",
  "Receptionist",
  "Facility_Admin",
  "Psychologist",
];

const RoleForm = ({
  userRoles,
  clinicalRoles = [],
  editListData,
  selectedRoles,
  setSelectedRoles,
  form,
  facilities,
  params,
  userDetails,
}) => {
  useEffect(() => {
    if (editListData?.roles) {
      const initialSelectedRoles = editListData?.roles?.map(
        (role) => role?.role
      );
      setSelectedRoles(initialSelectedRoles);
    }
  }, [editListData]);

  const defaultRole = userDetails?.currRole || getCookie(Role);

  const filteredWithoutHodRoles =
    defaultRole === "Facility_Admin"
      ? userRoles?.filter(
          (role) =>
            role?.value !== "Organization_Owner" &&
            role?.value !== "Organization_Admin"
        )
      : defaultRole === "Organization_Admin"
      ? userRoles?.filter((role) => role?.value !== "Organization_Owner")
      : userRoles;

  const handleRoleChange = (index, value) => {
    const updatedRoles = [...selectedRoles];
    updatedRoles[index] = value;
    setSelectedRoles(updatedRoles);

    if (editListData) {
      const newValues = [...form.getFieldsValue().roles];
      newValues[index].is_hod = null;
      form.setFieldsValue({ roles: newValues });
    }
  };

  const handleRemove = (name, index) => {
    const updatedRoles = [...selectedRoles];
    updatedRoles.splice(index, 1);
    setSelectedRoles(updatedRoles);

    form.setFieldsValue({
      roles: form.getFieldsValue().roles.filter((_, idx) => idx !== name),
    });
  };

  const roleRows = Form.useWatch("roles", form) || [];

  const facilityOf = (row) => row?.facility ?? null;

  const otherRows = (index) =>
    roleRows.filter((row, i) => i !== index && row?.role);

  const getAvailableRoles = (index) => {
    const thisFacility = facilityOf(roleRows[index]);
    const takenAtThisFacility = otherRows(index)
      .filter((row) => facilityOf(row) === thisFacility)
      .map((row) => row.role);

    return filteredWithoutHodRoles.filter(
      (role) => !takenAtThisFacility.includes(role.value)
    );
  };

  const getAvailableClinicalRoles = (index) => {
    const thisFacility = facilityOf(roleRows[index]);
    const takenAtThisFacility = otherRows(index)
      .filter((row) => isClinicalValue(row.role))
      .filter((row) => facilityOf(row) === thisFacility)
      .map((row) => row.role);
    return clinicalRoles.filter(
      (r) => !takenAtThisFacility.includes(`${CLINICAL_PREFIX}${r.id}`)
    );
  };


  const getAvailableFacilities = (index) => {
    const thisRole = roleRows[index]?.role;
    if (!thisRole) return facilities || [];
    const usedFacilityIds = otherRows(index)
      .filter((row) => row.role === thisRole)
      .map((row) => row.facility)
      .filter((id) => id !== undefined && id !== null);
    return (facilities || []).filter(
      (facility) => !usedFacilityIds.includes(facility?.id)
    );
  };

  // roles for which is_hod option to be visible. Clinical roles are
  // effectively Clinical_Practitioner, so they get the hod question too.
  const shouldDisplayHodField = (role) => {
    if (isClinicalValue(role)) return true;
    return (
      rolesWithIsHodQuestion.includes(role) &&
      !rolesWithoutHodField.includes(role)
    );
  };
  // roles for which facility option to be visible
  const shouldDisplayFacilityField = (role) => {
    if (isClinicalValue(role)) return true;
    return rolesWithFacility.includes(role);
  };

  return (
    <Form.List name="roles">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }, index) => {
            const showFacility =
              shouldDisplayFacilityField(selectedRoles[index]) &&
              defaultRole !== "Facility_Admin";
            const showHod = shouldDisplayHodField(selectedRoles[index]);
            return (
              <div key={key} style={{ marginBottom: 16 }}>
                <Flex gap={16} align="start" wrap>
                  <Form.Item
                    label="Role"
                    name={[name, "role"]}
                    rules={validateConfig.isRequired("Role")}
                    style={{
                      flex: "0 0 300px",
                      maxWidth: 300,
                      marginBottom: 0,
                    }}
                  >
                    <Select
                      placeholder="Choose role"
                      className={inputStyles.mdSelect}
                      optionFilterProp="label"
                      showSearch
                      onChange={(value) => handleRoleChange(index, value)}
                    >
                      <OptGroup label="Roles">
                        {getAvailableRoles(index).map((role) => (
                          <Option
                            value={role.value}
                            key={role.value}
                            label={role.label}
                          >
                            {role.label}
                          </Option>
                        ))}
                      </OptGroup>
                      {getAvailableClinicalRoles(index).length > 0 && (
                        <OptGroup label="Clinical Roles">
                          {getAvailableClinicalRoles(index).map((r) => (
                            <Option
                              value={`${CLINICAL_PREFIX}${r.id}`}
                              key={`${CLINICAL_PREFIX}${r.id}`}
                              label={r.name}
                            >
                              {r.name}
                            </Option>
                          ))}
                        </OptGroup>
                      )}
                    </Select>
                  </Form.Item>

                  {showFacility && (
                    <Form.Item
                      label="Facility"
                      name={[name, "facility"]}
                      rules={validateConfig.isRequired("Facility")}
                      style={{
                        flex: "0 0 300px",
                        maxWidth: 300,
                        marginBottom: 0,
                      }}
                    >
                      <Select
                        placeholder="Choose facility"
                        className={inputStyles.mdSelect}
                        optionFilterProp="children"
                      >
                        {getAvailableFacilities(index).map((facility) => (
                          <Option value={facility?.id} key={facility?.id}>
                            {facility?.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}

                  <DeleteOutlined
                    onClick={() => handleRemove(name, index)}
                    style={{
                      alignSelf: "center",
                      marginTop: 24,
                      color: "#667085",
                      cursor: "pointer",
                    }}
                  />
                </Flex>

                {showHod && (
                  <Form.Item
                    label="Is hod?"
                    {...restField}
                    name={[name, "is_hod"]}
                    rules={[{ required: true, message: "Please choose" }]}
                    style={{ marginTop: 12, marginBottom: 0 }}
                  >
                    <Radio.Group>
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                )}
              </div>
            );
          })}
          <Form.Item>
            <Button
              type="dashed"
              style={{ width: "50%" }}
              onClick={() => {
                add();
                setSelectedRoles([...selectedRoles, null]); // Add a placeholder for the new role
              }}
              block
              icon={<PlusOutlined />}
            >
              Add role
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );
};

export default RoleForm;
